import type {
  AllowedChargingPower,
  ChargingPlanRequest,
} from '../../../types/charging-steering.types'
import type { PlanStepDraft } from '../chargingSteeringSlice'

export type PlanDraft = {
  start: string
  steps: PlanStepDraft[]
}

export type PlanValidationResult =
  | { success: true }
  | {
      success: false
      errorMessage: string
    }

export function formatAllowedChargingPower(allowedChargingPower?: AllowedChargingPower): string {
  if (!allowedChargingPower) {
    return '-'
  }

  if ('levels' in allowedChargingPower) {
    return allowedChargingPower.levels.map((level) => `${level} W`).join(', ')
  }

  return `0 W, ${allowedChargingPower.interval.min} W - ${allowedChargingPower.interval.max} W`
}

export function validatePlanDraft(
  draft: PlanDraft,
  allowedChargingPower?: AllowedChargingPower,
): PlanValidationResult {
  if (!draft.start) {
    return { success: false, errorMessage: 'Please select a start time.' }
  }

  if (draft.steps.length === 0) {
    return { success: false, errorMessage: 'Please add at least one section.' }
  }

  for (let i = 0; i < draft.steps.length; i += 1) {
    const step = draft.steps[i]
    const duration = Number(step.duration)
    const chargePower = Number(step.chargePower)

    if (!Number.isInteger(duration) || duration <= 0) {
      return {
        success: false,
        errorMessage: `Section ${i + 1}: duration must be a positive integer.`,
      }
    }

    if (!Number.isInteger(chargePower) || chargePower < 0) {
      return {
        success: false,
        errorMessage: `Section ${i + 1}: charge power must be a non-negative integer.`,
      }
    }

    if (allowedChargingPower && 'levels' in allowedChargingPower) {
      const allowedPowerLevels = allowedChargingPower.levels
      if (!allowedPowerLevels.includes(chargePower)) {
        return {
          success: false,
          errorMessage: `Section ${i + 1}: charge power must be one of ${allowedPowerLevels.join(
            ', ',
          )} W.`,
        }
      }
    }

    if (allowedChargingPower && 'interval' in allowedChargingPower) {
      const { min, max } = allowedChargingPower.interval
      if ((chargePower < min || chargePower > max) && chargePower !== 0) {
        return {
          success: false,
          errorMessage: `Section ${i + 1}: charge power must be between ${min} W and ${max} W or 0W.`,
        }
      }
    }
  }

  return { success: true }
}

export function toChargingPlanRequest(draft: PlanDraft): ChargingPlanRequest {
  return {
    start: new Date(draft.start).toISOString(),
    steps: draft.steps.map((step) => ({
      duration: Number(step.duration),
      chargePower: Number(step.chargePower),
    })),
  }
}

export function calculatePlanTotalMinutes(steps: PlanStepDraft[]): number {
  return steps.reduce((sum, step) => {
    const parsedDuration = Number(step.duration)
    return Number.isFinite(parsedDuration) ? sum + parsedDuration : sum
  }, 0)
}

export function calculatePlanTotalEnergyWh(steps: PlanStepDraft[]): number {
  return steps.reduce((sum, step) => {
    const duration = Number(step.duration)
    const chargePower = Number(step.chargePower)

    if (!Number.isFinite(duration) || !Number.isFinite(chargePower)) {
      return sum
    }

    return sum + chargePower * (duration / 60)
  }, 0)
}

export function calculatePlanEndTime(start: string, totalMinutes: number): string {
  if (!start) {
    return '-'
  }

  const startDate = new Date(start)
  if (Number.isNaN(startDate.getTime())) {
    return '-'
  }

  return new Date(startDate.getTime() + totalMinutes * 60 * 1000).toLocaleString()
}
